/**
 * OverlayManager (singleton)
 *
 * Purpose
 * -------
 * Centralized manager for a single shared overlay DOM node used across the
 * theme to block background interactions and receive outside-click events.
 * It prevents multiple components from creating competing overlays and
 * provides ownership semantics so multiple components can safely request the
 * overlay without stomping on each other.
 *
 * Key concepts / API
 * ------------------
 * - show() -> ownerToken (string)
 *     Request the overlay to be visible. Returns a token representing the
 *     ownership of that visibility request. Callers should keep the token
 *     and pass it to hide(token) when they no longer need the overlay.
 *
 * - hide(token?)
 *     Release a previously-acquired owner token. If a token is provided the
 *     specific owner is removed; if no token is provided the method falls
 *     back to removing a single owner (legacy-compatible behavior).
 *
 * - add(handler) / remove(handler)
 *     Register a click handler that runs when the overlay is clicked. Handlers
 *     are separate from ownership: calling add() makes the overlay visible so
 *     the handler can be clicked, but it does not create an ownership token.
 *     remove() removes a handler. If there are no owners and no handlers the
 *     overlay is hidden.
 *
 * - getState()
 *     Dev helper that returns internal counts (owners, handlers, attached).
 *
 * Semantics & design notes
 * ------------------------
 * - Ownership vs handlers: ownership (show/hide) controls visual state via a
 *   Map of owner tokens. Handlers are callbacks registered separately so a
 *   component can opt-in to outside-click behavior without being responsible
 *   for overlay ownership.
 * - Defensive click behavior: when the overlay is clicked, all registered
 *   handlers will be invoked. The overlay also clears handlers and owner
 *   state defensively to avoid becoming stuck if some caller fails to clean
 *   up. This makes the overlay resilient in mixed/legacy code paths.
 * Accessibility
 * -------------
 * - The overlay sets `aria-hidden` when visible/hidden. For full a11y
 *   compatibility consider making background content inert (or toggling
 *   `aria-hidden` on main content) when a drawer is open. This manager does
 *   not implement `inert` automatically (polyfill required in some browsers).
 *
 * Migration notes
 * ---------------
 * - Old code that called show()/hide() without tokens will continue to work.
 *   New callers should capture the token returned by show() and provide it
 *   to hide(token) for deterministic cleanup. Example:
 *
 *     const owner = OverlayManager.show();
 *     OverlayManager.add(() => closeDrawer());
 *     // later...
 *     OverlayManager.remove(handler);
 *     OverlayManager.hide(owner);
 *
 * - FocusTrap integration: FocusTrap now captures the token returned from
 *   OverlayManager.show() and uses it to hide the overlay on deactivate.
 */

const OverlayManager = (() => {
  // Use a global singleton to ensure all bundles share the same state
  if (window.__OverlayManager) {
    return window.__OverlayManager;
  }

  let overlay = null;
  // ownerId -> active
  const owners = new Map();
  let _nextId = 1;
  const handlers = new Set();
  // Pending frame from showOverlay()'s deferred reveal (see below) — cancelled
  // by hideOverlay() so a close in the same frame can't have the stale
  // callback re-reveal the dim after it was already cleared.
  let _revealFrame = null;

  function showOverlay() {
    overlay.style.pointerEvents = 'auto';
    try {
      if (!overlay.matches(':popover-open')) overlay.showPopover();
    } catch (e) { /* Popover API unavailable: shared z-index rules are the fallback. */ }
    // Promoting to the top layer and revealing (aria-hidden=false, which drives
    // the opacity transition) in the same tick leaves nothing for the browser
    // to have painted at opacity: 0 — the dim snaps straight to visible instead
    // of fading in. Defer the reveal a frame so the closed state paints first,
    // matching how BaseDrawer.open() defers its own reveal.
    if (_revealFrame) cancelAnimationFrame(_revealFrame);
    _revealFrame = requestAnimationFrame(() => {
      _revealFrame = null;
      overlay.setAttribute('aria-hidden', 'false');
    });
  }

  function hideOverlay() {
    if (_revealFrame) {
      cancelAnimationFrame(_revealFrame);
      _revealFrame = null;
    }
    overlay.setAttribute('aria-hidden', 'true');
    overlay.style.pointerEvents = 'none';
    try {
      if (overlay.matches(':popover-open')) overlay.hidePopover();
    } catch (e) { /* Popover API unavailable. */ }
  }

  function ensure() {
    // Reattach the shared node if a legacy section morph removed it.
    if (overlay && !overlay.isConnected) {
      document.body.appendChild(overlay);
    }
    if (overlay) return overlay;
    overlay = document.querySelector('[data-focus-overlay]');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.setAttribute('data-focus-overlay', '');
      overlay.setAttribute('popover', 'manual');
      overlay.className =
        'fixed top-0 left-0 h-screen min-h-[-webkit-fill-available] w-full bg-black/30 transition-opacity duration-300 pointer-events-none aria-hidden:opacity-0 aria-hidden:duration-0 aria-[hidden=false]:pointer-events-auto aria-[hidden=false]:cursor-pointer';
      overlay.style.position = 'fixed';
      overlay.style.inset = '0';
      overlay.style.background = 'rgba(0, 0, 0, 0.3)';
      overlay.style.pointerEvents = 'none';
      overlay.setAttribute('aria-hidden', 'true');
      overlay.setAttribute('aria-label', 'Close');
      overlay.setAttribute('role', 'button');
      document.body.appendChild(overlay);
    } else {
      overlay.setAttribute('popover', 'manual');
    }

    if (!overlay._attached) {
      // Use passive listener for better scroll performance, capture for faster response
      overlay.addEventListener('click', (e) => {
        // Call all registered handlers (copy to avoid mutation issues)
        const copy = Array.from(handlers);
        for (const fn of copy) {
          try { fn(e); } catch (err) { console.error('[OverlayManager] handler error', err); }
        }
      }, { passive: true, capture: false });
      overlay._attached = true;
    }

    return overlay;
  }

  function restoreAfterMorph() {
    if (!overlay || overlay.isConnected) return;

    const shouldRestore = owners.size > 0 || handlers.size > 0;
    if (!shouldRestore) return;

    ensure();
    showOverlay();
  }

  document.addEventListener('theme:section:morph', restoreAfterMorph);
  document.addEventListener('shopify:section:load', restoreAfterMorph);

  return {
    add(handler) {
      if (typeof handler !== 'function') return;
      ensure();
      handlers.add(handler);
      // Do not modify the refcount when registering a handler. show()/hide()
      // manages the visual refcount. Ensure overlay is visible so handler can be clicked.
      showOverlay();
    },
    remove(handler) {
      if (!overlay) return;
      if (typeof handler === 'function') handlers.delete(handler);
      // Only hide if there are no show() owners and no handlers left
      if (owners.size === 0 && handlers.size === 0) {
        hideOverlay();
      }
    },
    show() {
      ensure();
      const id = String(_nextId++);
      owners.set(id, true);
      showOverlay();
      return id;
    },
    hide(token) {
      if (!overlay) return;

      // Backwards compatible: if no token provided, behave like previous
      // hide() by removing one arbitrary owner (the most recent), otherwise
      // remove the given token.
      if (typeof token === 'undefined') {
        // remove a single owner if present
        const iter = owners.keys();
        const first = iter.next();
        if (!first.done) owners.delete(first.value);
      } else {
        owners.delete(String(token));
      }

      // Only hide overlay if no show() owners and no registered handlers
      if (owners.size === 0 && handlers.size === 0) {
        hideOverlay();
      }
    },
    // Debug helper - return internal state useful for tests or debugging
    getState() {
      return {
        owners: owners.size,
        handlers: handlers.size,
        attached: !!(overlay && overlay._attached),
        ariaHidden: overlay ? overlay.getAttribute('aria-hidden') : null
      };
    },
    getOverlay() {
      if (overlay) return overlay;
      const found = document.querySelector('[data-focus-overlay]');
      if (found) overlay = found;
      return overlay;
    }
  };
})();

// Store globally so all bundles share the same instance
if (!window.__OverlayManager) {
  window.__OverlayManager = OverlayManager;
}

export default OverlayManager;
